import { CreditPlanDebug, CreditRuleValidationSummary } from '@/@types/client-api';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import Accordion from '../Accordion';
import RichText from '@/components/cms/RichText';
import Text from '@/components/cms/Text';
import { format } from 'date-fns';
import Icon from '../Icon';
import ComplexTooltip from '../Tooltip/ComplexTooltip';
import Divider from '../Divider';

type CreditPlanDebugProps = CreditPlanDebug & {
  className?: string;
  planId: string;
};

type RequirementGroup = {
  [key: string]: {
    type: string;
    amountRequired: number;
    amountEarned: number;
    message: string;
  }[];
};

const DebugInformation: FC<CreditPlanDebugProps> = ({
  allowed,
  className,
  model,
  prompt,
  systemPrompt,
  reasoning,
  totalProcessingTime,
  reasoningExplanation,
  totalSearchResults,
  creditRuleValidationSummary,
  allCreditRuleValidationSummaries,
  metadata,
  planId
}) => {
  const [contentContainer, setContentContainer] = useState<HTMLDivElement | null>(null);

  const parsePrompt = useCallback((str: string) => {
    if (!str) return;
    let html = str;
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');
    html = html.replace(/`(.*?)`/gim, '<code>$1</code>');
    html = html.replace(/\n/gim, '<br />');
    return html;
  }, []);

  const metadataRows = [
    { label: 'Plan Id', value: planId },
    { label: 'Model', value: model },
    { label: 'Reasoning', value: reasoning?.replace('Reasoning:', '') },
    { label: 'Total search results', value: totalSearchResults },
    {
      label: 'Total processing time',
      value: totalProcessingTime ? format(new Date(totalProcessingTime), 'mm:ss') : null
    },
    ...Object.keys(metadata ?? {}).map(a => ({ label: a, value: metadata?.[a] }))
  ].filter(row => !!row.value);

  const accordionSections = useMemo(() => {
    return [
      { label: 'Reasoning explanation', content: reasoningExplanation },
      { label: 'System Prompt', content: parsePrompt(systemPrompt ?? '') },
      { label: 'Prompt', content: parsePrompt(prompt ?? '') }
    ].filter((section): section is { label: string; content: string } => !!section.content);
  }, [parsePrompt, prompt, reasoningExplanation, systemPrompt]);

  const allCreditRuleValidations = useMemo(() => {
    return (
      allCreditRuleValidationSummaries ??
      (creditRuleValidationSummary ? [creditRuleValidationSummary] : [])
    );
  }, [allCreditRuleValidationSummaries, creditRuleValidationSummary]);

  const getGroupedRequirements = useCallback(
    (creditRuleValidationSummary: CreditRuleValidationSummary) => {
      const groupedRequirements: RequirementGroup = {};

      creditRuleValidationSummary?.requirements?.forEach(requirement => {
        const requirementType = String(requirement.validationRequirementType);
        const existing = groupedRequirements[requirementType];

        groupedRequirements[requirementType] = [
          ...(existing ?? []),
          {
            type: requirement.typeId,
            amountRequired: requirement.amountRequired || 0,
            amountEarned: requirement.amountEarned || 0,
            message: requirement.message
          }
        ];
      });

      return groupedRequirements;
    },
    []
  );

  const renderCreditRuleValidationSummary = useCallback(
    (creditRuleValidationSummary: CreditRuleValidationSummary) => {
      const groupedRequirements = getGroupedRequirements(creditRuleValidationSummary);
      const requirementObjectKeys = Object.keys(groupedRequirements || {});
      return requirementObjectKeys
        ?.sort((a, _) => (a.toLowerCase() == 'total' ? -1 : 0))
        ?.map(key => {
          const current = groupedRequirements[key];
          return (
            <Fragment key={key}>
              {current.map((requirement, index) => {
                const fulfilled = requirement.amountEarned >= requirement.amountRequired;
                const classes = classnames('ml-2', {
                  'text-red': !fulfilled && key.toLowerCase() != 'total',
                  '!text-2-bold text-green-300': fulfilled && key.toLowerCase() == 'total'
                });
                return (
                  <Fragment key={index}>
                    <div className={classes}>
                      <RichText content={requirement.type} className="text-2-bold my-3" />
                      <table className="text-sm hidden w-full text-left sm:table">
                        <tbody>
                          <tr className="border-t">
                            <td className="text-2-medium w-1/3 whitespace-nowrap border-r px-2">
                              Amount Required
                            </td>
                            <td className="p-2">{requirement.amountRequired.toFixed(2)}</td>
                          </tr>
                          <tr className="border-y">
                            <td className="text-2-medium w-1/3 whitespace-nowrap border-r px-2">
                              Amount Earned
                            </td>
                            <td className="p-2">{requirement.amountEarned.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                      {!fulfilled && (
                        <RichText content={requirement.message} className="text-2 my-3 italic" />
                      )}
                    </div>
                    <Divider color="darker" className="mb-5 mt-2" />
                  </Fragment>
                );
              })}
            </Fragment>
          );
        });
    },
    [getGroupedRequirements]
  );

  const creditRuleValidationAccordions = useMemo(() => {
    return (
      !!allCreditRuleValidations &&
      (allCreditRuleValidations?.length ?? 0) > 0 &&
      allCreditRuleValidations
        ?.filter(a => (a?.requirements?.length || 0) > 0)
        ?.map((summary, i) => (
          <Accordion
            key={`summary-${i}`}
            type="single"
            classNames="mt-2 flex flex-col rounded-[4px] bg-gray-feather px-5"
            items={[
              {
                className: 'group/rule-validation-accordion',
                header: (
                  <span className="text-2-bold inline-flex items-center justify-start pr-6">
                    <span className="mr-2">Attempt {i + 1}: Requirements Validation Summary</span>
                    {summary.failed ? (
                      <Icon name="alert" size="medium" className=" text-red" />
                    ) : (
                      <Icon name="checkmark" size="medium" className=" text-green-300" />
                    )}
                  </span>
                ),
                content: [<div className="mb-6">{renderCreditRuleValidationSummary(summary)}</div>]
              }
            ]}
          />
        ))
    );
  }, [allCreditRuleValidations, renderCreditRuleValidationSummary]);

  const helpTooltip = useMemo(() => {
    return {
      children: (
        <>
          <ComplexTooltip.Trigger className="z-1">
            <Icon name="info" size="small" className="text-gray" />
          </ComplexTooltip.Trigger>

          <ComplexTooltip.Content customContainer={contentContainer}>
            <div>
              <p className="text-2 mb-2">
                <span className="text-2-bold mr-1">System Prompt:</span> added as the System Message
                with the LLM.
                <br />
                <br />
                <span className="text-2-bold mr-1">Prompt:</span> added as a User Message with the
                LLM.
              </p>
            </div>
          </ComplexTooltip.Content>
        </>
      )
    };
  }, [contentContainer]);

  if (!allowed) return null;

  return (
    <div className="relative mt-3 overflow-hidden">
      <div
        className={classnames(
          'my-6 flex items-center rounded p-[0.95px]',
          'bg-gradient-to-tr from-[#FFC600] via-red to-blue-400',
          'origin-top duration-long animate-in fade-in-0 slide-in-from-top'
        )}
      >
        <Accordion
          type="single"
          classNames={classnames(
            '!m-0 flex flex-col rounded-[4px] bg-gray-feather px-5',
            className
          )}
          headerClassNames="w-fit"
          items={[
            {
              className: 'group/debug-accordion',
              header: (
                <span className="mr-4 flex flex-row items-center justify-start">
                  <Icon name="info" size="medium" className="mr-2" />
                  <span className="text-2-bold flex items-center justify-start pr-6">
                    <span className="mr-2">Debug information</span>
                    <ComplexTooltip {...helpTooltip} variant="gray" />
                  </span>
                </span>
              ),
              content: [
                <div
                  className={classnames(
                    'mb-4 flex flex-col items-start rounded p-px',
                    'bg-gradient-to-tr from-[#FFC600] via-red to-blue-400'
                  )}
                >
                  <div className="w-full bg-white p-2">
                    {metadataRows.length > 0 && (
                      <div className="mb-6">
                        <table className="text-sm hidden w-full text-left sm:table">
                          <tbody>
                            {metadataRows.map(({ label, value }) => (
                              <tr key={label} className="border-b">
                                <td className="text-2-bold whitespace-nowrap border-r px-2">
                                  {label}
                                </td>
                                <td className="p-2">
                                  <Text>{value}</Text>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <dl className="flex flex-col gap-2 sm:hidden">
                          {metadataRows.map(({ label, value }) => (
                            <div key={`${label}-hidden`} className="border-b pb-2">
                              <dt className="text-2-bold">{label}</dt>
                              <dd className="mt-1 pl-2">
                                <Text>{value}</Text>
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                    <Divider color="silver" />
                    {creditRuleValidationAccordions}
                    {accordionSections.map(({ label, content }) => (
                      <Accordion
                        key={label}
                        type="single"
                        classNames="mt-2 flex flex-col rounded-[4px] bg-gray-feather px-5"
                        items={[
                          {
                            className: 'group/debug-accordion',
                            header: <span className="text-2-bold pr-6">{label}</span>,
                            content: [<RichText content={content} className="p-2" />]
                          }
                        ]}
                      />
                    ))}
                  </div>
                </div>
              ]
            }
          ]}
        />
        <div ref={setContentContainer} />
      </div>
    </div>
  );
};

export default DebugInformation;
